/**
 * Mentions dans un post : « @Sam.78 ».
 *
 * Le contenu reste du texte brut en base — c'est ce que lisent la
 * recherche, l'apercu d'une notification et tout ce qui viendra ensuite.
 * Les membres cites sont ranges a cote, dans une petite liste : elle sert
 * a savoir qui prevenir, et a faire d'une mention un lien vers le bon
 * profil sans avoir a resoudre un pseudo a chaque affichage.
 *
 * Un pseudo Sirocco contient des points ("Sam.78") : impossible d'en faire
 * une cle Firebase, d'ou la liste plutot qu'un objet.
 */

import { normalizeUsername } from '../firebase/paths';

/** Un membre cite dans un post. */
export interface Mention {
  id: string;
  username: string;
}

/**
 * Ce qui, juste avant un `@`, en fait une adresse plutot qu'une mention.
 *
 * La fin d'un nom de boite : une lettre, un chiffre, ou l'un des signes
 * qu'on y trouve. « nssm@mail.com » n'est pas une mention.
 *
 * Tout le reste ouvre bien une mention, et c'est le point : une
 * parenthese, un guillemet, un tiret, une puce de liste. Exiger une
 * espace laissait « (@Sam) » et « — @Sam » de cote, alors qu'on ecrit
 * comme ca.
 */
const FIN_D_ADRESSE = /[\p{L}\p{N}._+-]/u;

/** Ou commence une mention. */
function arobases(content: string): number[] {
  const places: number[] = [];
  for (let at = content.indexOf('@'); at !== -1; at = content.indexOf('@', at + 1)) {
    if (at === 0 || !FIN_D_ADRESSE.test(content[at - 1]!)) places.push(at);
  }
  return places;
}

/** Un morceau de contenu : du texte, ou un membre cite. */
export type Piece =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; text: string; mention: Mention };

/**
 * Decoupe un contenu en morceaux, prêts a etre rendus.
 *
 * # Pourquoi l'on compare des pseudos entiers, et non un mot
 *
 * Rien n'encadre ce qu'un pseudo contient. Il y en a avec un accent
 * ("Chaïma.H"), avec un plus ("FAN2ADAM+BARTIER"), avec une espace
 * ("Ninja taga"), et un qui n'est qu'un emoji. Toute tentative de
 * decrire un pseudo par une liste de caracteres s'arrete au premier qui
 * n'y figure pas : "@Ninja taga" ne citait que "Ninja", "@Chaïma.H" que
 * "Cha", et ni l'un ni l'autre ne correspond a personne.
 *
 * On lit donc dans l'autre sens. Les pseudos cites sont connus : a
 * chaque `@`, on regarde lequel d'entre eux commence le texte qui suit.
 * Les plus longs d'abord, sans quoi "Ninja" l'emporterait sur "Ninja
 * taga" des que les deux existent.
 *
 * Seuls les pseudos de `mentions` sont reconnus : le reste demeure du
 * texte, et c'est `PostText` qui va chercher ce qui manque.
 */
export function splitMentions(content: string, mentions: Mention[]): Piece[] {
  if (!mentions.length) return content ? [{ kind: 'text', text: content }] : [];

  const connus = mentions
    .map((mention) => ({
      mention,
      /* Sur la longueur du pseudo nu : la cle d'unicite retire les
         espaces de bout, et un pseudo en porte parfois une. */
      long: mention.username.trim().length,
      cle: normalizeUsername(mention.username),
    }))
    .filter((candidat) => candidat.long > 0)
    .sort((a, b) => b.long - a.long);

  const pieces: Piece[] = [];
  let cursor = 0;

  for (const at of arobases(content)) {
    // Un `@` avale par la mention precedente ne compte pas.
    if (at < cursor) continue;
    const reste = content.slice(at + 1);
    const trouve = connus.find(
      (candidat) => normalizeUsername(reste.slice(0, candidat.long)) === candidat.cle,
    );
    if (!trouve) continue;

    const ecrit = reste.slice(0, trouve.long);
    if (at > cursor) pieces.push({ kind: 'text', text: content.slice(cursor, at) });
    pieces.push({ kind: 'mention', text: `@${ecrit}`, mention: trouve.mention });
    cursor = at + 1 + ecrit.length;
  }

  if (cursor < content.length) pieces.push({ kind: 'text', text: content.slice(cursor) });
  return pieces;
}

/** Au-dela, ce n'est plus un pseudo mais une phrase. */
const MOTS_MAX = 4;

/**
 * La ponctuation qui suit un pseudo sans lui appartenir.
 *
 * On ne risque rien a en retirer trop : la forme entiere est proposee
 * avant la forme rognee, et c'est l'annuaire qui tranche. Un pseudo qui
 * finit vraiment par un point sera donc trouve tel quel.
 */
const FIN_DE_PHRASE = /[.,;:!?…«»"'’“”)\]}>*~|/\\-]+$/u;

/**
 * Les pseudos ecrits a la main, tels qu'il faudrait les chercher.
 *
 * Une mention ne devient un lien que si le membre est reconnu, et
 * l'affichage ne connait au depart que la liste rangee avec le post.
 * Pour tout le reste, il faut interroger l'annuaire — encore faut-il
 * savoir quoi lui demander.
 *
 * Comme un pseudo peut contenir une espace, on ne peut pas se fier au
 * mot suivant : "@Ninja taga" est un seul pseudo, "@Idris il tape" n'en
 * est qu'un d'un mot suivi d'une phrase. On propose donc plusieurs
 * decoupes, de la plus longue a la plus courte, et c'est l'annuaire qui
 * tranche. Quatre mots au plus : au-dela, c'est une phrase.
 *
 * Chaque decoupe vient aussi sans sa ponctuation finale : "@Sam." en fin
 * de phrase cite Sam.
 */
export function nomsPossibles(content: string): string[][] {
  const trouves: string[][] = [];

  for (const at of arobases(content)) {
    /* Jusqu'au bout de la ligne, et pas au-dela du `@` suivant : deux
       mentions cote a cote n'ont pas a se manger l'une l'autre. */
    const ligne = (content.slice(at + 1).split('\n')[0] ?? '').split('@')[0] ?? '';
    const mots = ligne.split(' ');
    const candidats: string[] = [];

    for (let combien = Math.min(MOTS_MAX, mots.length); combien >= 1; combien -= 1) {
      const brut = mots.slice(0, combien).join(' ').trim();
      if (!brut) continue;
      if (!candidats.includes(brut)) candidats.push(brut);
      const rogne = brut.replace(FIN_DE_PHRASE, '').trim();
      if (rogne && !candidats.includes(rogne)) candidats.push(rogne);
    }

    if (candidats.length) trouves.push(candidats);
  }

  return trouves;
}

/**
 * Le pseudo en cours de frappe, s'il y en a un.
 *
 * On ne regarde que ce qui precede le curseur : c'est la que se trouve ce
 * qu'on est en train d'ecrire. Rend `null` des qu'un espace est passe —
 * une mention est un seul mot.
 */
export function typingMention(content: string, caret: number): string | null {
  const before = content.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;

  // La meme regle qu'a l'affichage : seule la fin d'un nom de boite
  // empeche une mention. Voir `FIN_D_ADRESSE`.
  if (at > 0 && FIN_D_ADRESSE.test(before[at - 1]!)) return null;

  const typed = before.slice(at + 1);
  // Tout sauf une espace : un pseudo peut porter un plus, un point, ou
  // n'etre qu'un emoji. Une mention en cours de frappe reste un mot —
  // celui qui en compte deux se choisit dans la liste, qui l'insere en
  // entier.
  return /^\S*$/u.test(typed) ? typed : null;
}

/**
 * Remplace le pseudo en cours de frappe par celui qu'on a choisi.
 * Rend le nouveau contenu et ou poser le curseur.
 */
export function insertMention(
  content: string,
  caret: number,
  username: string,
): { content: string; caret: number } {
  const before = content.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return { content, caret };

  // L'espace final evite d'avoir a le taper avant de continuer sa phrase.
  const head = `${content.slice(0, at)}@${username} `;
  return { content: head + content.slice(caret), caret: head.length };
}

/** Les membres cites qui restent dans le texte, une fois celui-ci fige. */
export function keepCited(content: string, mentions: Mention[]): Mention[] {
  const cited = splitMentions(content, mentions);
  const seen = new Set(
    cited.flatMap((piece) => (piece.kind === 'mention' ? [piece.mention.id] : [])),
  );
  return mentions.filter((mention) => seen.has(mention.id));
}
